import { HttpClient } from "@angular/common/http";
import { Injectable, Injector } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { Preferences } from "@capacitor/preferences";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private _currentUserSubject: BehaviorSubject<any | null>;
  public currentUser$: Observable<any | null>;

  // Public observable to check if a user is authenticated (has token and is verified from API)
  public isAuthenticated$: Observable<boolean>;

  constructor(private http: HttpClient) {
    const storedUser = this.getAuthUserFromStorage();
    this._currentUserSubject = new BehaviorSubject<any | null>(storedUser);
    this.currentUser$ = this._currentUserSubject.asObservable();

    this.isAuthenticated$ = this.currentUser$.pipe(
      map((user) => {
        const tokenExists =
          this.getAuthToken() !== null && this.getAuthToken() !== "null";
        // User is authenticated if they exist, have a token, and API says they're verified
        return user !== null && tokenExists && user?.is_verified === true;
      })
    );
  }

  // --- Private helper to get user from storage (sync fallback for initialization) ---
  // Note: This uses BehaviorSubject's initial value, which is loaded async in APP_INITIALIZER
  private getAuthUserFromStorage(): any | null {
    // Return null on initialization - actual restore happens in restoreSession()
    return null;
  }

  // --- Async storage methods (preferred) ---
  private async loadAuthState(): Promise<{
    user: any | null;
    token: string | null;
  }> {
    try {
      const [userResult, tokenResult] = await Promise.all([
        Preferences.get({ key: "auth-user" }),
        Preferences.get({ key: "auth-token" }),
      ]);

      return {
        user: userResult.value ? JSON.parse(userResult.value) : null,
        token: tokenResult.value || null,
      };
    } catch (error) {
      console.error("Error loading auth state:", error);
      return { user: null, token: null };
    }
  }

  // --- Sync getters (for backward compatibility and non-async contexts) ---
  // Note: Token is cached in memory after async load
  private cachedToken: string | null = null;

  public getAuthToken = (): string | null => {
    return this.cachedToken;
  };

  public getAuthUser = (): any | null => {
    return this._currentUserSubject.getValue();
  };

  // Check if user is verified from the user object itself (API is source of truth)
  public isUserVerified = (): boolean => {
    const user = this.getAuthUser();
    return user?.is_verified === true;
  };

  // --- Async setters (preferred - use these for all new code) ---
  public async setAuthToken(token: string): Promise<void> {
    await Preferences.set({ key: "auth-token", value: token });
    this.cachedToken = token; // Cache in memory for sync access
  }

  public async setAuthUser(user: any | null): Promise<void> {
    if (user) {
      await Preferences.set({ key: "auth-user", value: JSON.stringify(user) });
      this._currentUserSubject.next(user);
    } else {
      await Preferences.remove({ key: "auth-user" });
      this._currentUserSubject.next(null);
    }
  }

  // ====================== Helper method to check persistent login state on app startup
  public async restoreSession(): Promise<boolean> {
    try {
      const state = await this.loadAuthState();

      // Cache token in memory for sync access
      this.cachedToken = state.token;

      // User has valid session if they have token, user data, and API says they're verified
      if (state.token && state.user && state.user.is_verified === true) {
        this._currentUserSubject.next(state.user);
        return true;
      }

      return false;
    } catch (error) {
      console.error("Error restoring session:", error);
      return false;
    }
  }

  public async logOutUser(): Promise<void> {
    try {
      await Promise.all([
        Preferences.remove({ key: "auth-token" }),
        Preferences.remove({ key: "auth-user" }),
      ]);

      // Clear memory cache
      this.cachedToken = null;
      this._currentUserSubject.next(null);
    } catch (error) {
      console.error("Error logging out:", error);
      this.cachedToken = null;
      this._currentUserSubject.next(null);
    }
  }

  /**
   * Fetches the current IP address from an external service.
   * @returns {Promise<any>} A promise that resolves with the IP address data.
   */
  public getIPAddress(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http?.get("http://ip-api.com/json").subscribe({
        next: (res: any) => resolve(res),
        error: (err) => reject(err),
      });
    });
  }
  
}
